import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Layout, Button, Input, Select } from '../../../components/';
import SelectType from '../../../components/Form/Select/select_type';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { FaqDataDTO } from '../../../shared/interfaces/faq';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Container, FormContainer, FormFooter } from './styles';

const Perguntas: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();

  const [dataTable, setDataTable] = useState<SelectType[]>([]);
  let selectedScreens = useRef<number[] | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get<ScreenData[]>(`/tela`);

      let screenValues: SelectType[] = response.data.map(
        (screen: ScreenData) => {
          return {
            value: screen.id,
            label: screen.titulo,
          };
        },
      );

      setDataTable(screenValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function back() {
    if (location.pathname.match('/listar')) {
      history.push(RoutesPaths.home);
    } else {
      history.goBack();
    }
  }

  const handleSubmit = useCallback(
    async (data: FaqDataDTO) => {
      setIsLoading(true);
      data.idTelas = selectedScreens.current;

      try {
        if (!data.idTelas?.length) {
          const schemaSelect = Yup.object().shape({
            pergunta: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            resposta: Yup.string()
              .max(400, 'No máximo 400 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect.validate(data, {
            abortEarly: false,
          });
        }
        const schema = Yup.object().shape({
          id: Yup.string().max(25, 'No máximo 25 caracteres'),
          pergunta: Yup.string()
            .max(200, 'No máximo 200 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
          resposta: Yup.string()
            .max(400, 'No máximo 400 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api.post('/faq', data);

        setIsLoading(false);

        addToast({
          type: 'success',
          title: 'Pergunta cadastrada com sucesso!',
          description:
            'As informações da pergunta foram cadastradas. Você pode verificar a nova pergunta na seção de Listagem de Perguntas.',
        });

        setTimeout(() => {
          history.push(RoutesPaths.listFAQ);
        }, 4000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setIsLoading(false);
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Erro ao cadastrar a Pergunta',
            description: 'Verifique as informações inseridas.',
          });
        }
      }
    },
    [addToast, history],
  );

  function handleScreenChange(dataTable: any | null) {
    if (dataTable) {
      const valor = dataTable.map((item: any) => item['value']);
      selectedScreens.current = valor;
    }
  }

  return (
    <>
      <Layout title={'Cadastro de Perguntas'} hideFloatingButton>
        <Container>
          <FormContainer>
            <Form
              className="form-container"
              ref={formRef}
              onSubmit={handleSubmit}
            >
              <Input
                name="pergunta"
                label="Pergunta"
                type="text"
                data-testid={'form-field-question'}
              />
              <Input
                name="resposta"
                label="Resposta"
                type="text"
                data-testid={'form-field-awnser'}
              />
              <Select
                name={'select'}
                options={dataTable}
                onChange={handleScreenChange}
                placeholder="Selecione"
                label="Telas"
                value={dataTable}
                loading={!dataTable.length ? true : false}
              />

              <FormFooter>
                <Button
                  variant="solid"
                  style={{ marginTop: '1rem' }}
                  type="submit"
                  data-testid={'form-field-button'}
                  loading={isLoading}
                  disabled={!dataTable.length}
                >
                  Adicionar
                </Button>
                <Button
                  variant="outline"
                  color={'#C71F37'}
                  style={{ marginTop: '1rem' }}
                  onClick={() =>
                    setTimeout(() => {
                      back();
                    }, 300)
                  }
                >
                  Cancelar
                </Button>
              </FormFooter>
            </Form>
          </FormContainer>
        </Container>
      </Layout>
    </>
  );
};

export default Perguntas;
